import { useLayoutEffect, useRef, useState, type DragEvent, type KeyboardEvent, type PointerEvent } from 'react'
import { flushSync } from 'react-dom'
import type { DividerBlock, ImageBlock, ImageFrame, ImagePlacement, LinkCardBlock, Source, ThoughtBlock } from '../../lib/types'
import { hostOf, thoughtStamp } from '../../lib/text'
import { FramedImage } from '../FramedImage'
import { LinkSite } from '../LinkSite'
import { imageLayout } from '../imageLayout'
import { captionCls, OVERLAY_CLS, STAMP_CLS } from '../CaptionOverlay'
import { clampWidth, imageWidth, pastSlop, stepPlacement, widthRange, WIDTH_STEP, wrapOffset } from '../../lib/imageSize'
import { INLINE_REST_Y, MIN_CROP_BOX, safeFrame, shapeOf, visibleCrop, type CropRect } from '../../lib/frame'
import { useMediaQuery } from '../../lib/hooks'
import { useImageHand, type HandTarget } from './useImageHand'
import { playFlip, snapshotLayout } from './flip'
import { SourceForm, type SelectableProps, type SourceProps } from './ThoughtParts'
import { ImagePlacementToolbar } from './ImagePlacementToolbar'
import { BlockToolbar, RemoveButton, ToolbarButton, ToolbarDivider, ToolbarLabel } from './Toolbar'

export type { SelectableProps } from './ThoughtParts'
export { BlockToolbar, ToolbarButton, ToolbarDivider, ToolbarLabel } from './Toolbar'

function Meta({ capturedAt, kind }: { capturedAt?: string; kind?: string }) {
  if (!capturedAt) return null
  return (
    <span>
      {thoughtStamp(capturedAt)}
      {kind ? ` · ${kind}` : ''}
    </span>
  )
}

export function SourceLine({ source }: { source: Source }) {
  const site = hostOf(source.url)
  const title = source.title || site || source.url || ''
  const inner = (
    <>
      {title}
      {site && site !== title ? <span className="text-muted-foreground/70"> · {site}</span> : null}
    </>
  )
  return (
    <div className="flex min-w-0 items-center gap-1.5 font-sans text-[13px] font-medium text-muted-foreground">
      <span aria-hidden className="size-1.5 shrink-0 rounded-full bg-accent" />
      {source.url ? (
        <a href={source.url} target="_blank" rel="noopener noreferrer" className="min-w-0 truncate text-muted-foreground no-underline hover:underline">
          {inner}
        </a>
      ) : (
        <span className="min-w-0 truncate">{inner}</span>
      )}
    </div>
  )
}

/**
 * The grip on a block's left (images and cards alike): six dots, 2×3, close
 * against the block's left edge rather than out in the gutter (Niki, 10/2).
 * Drag it to move the block up or down, or beside another block as a new
 * column. A card's shows on hover and while it's selected (`shown` unset); an
 * image shows it with its other tools. It reaches across the gap to the
 * block, so the hover holds on the way to it.
 *
 * `onPointerDown` in place of `onDragStart`: the grip starts a drag of the
 * block's own (an image's hand, useImageHand) rather than the browser's.
 */
export function DragGrip({
  selected,
  shown,
  onDragStart,
  onPointerDown,
  reveal = 'card',
  className = 'top-3',
  title = 'Drag to move · beside another block makes columns',
}: {
  selected: boolean
  /** Shown now regardless of hover (the image's tools). */
  shown?: boolean
  /** What hovering shows it: its card (`group/card`), or its row in the composer (`group/row`, a paragraph's). */
  reveal?: 'card' | 'row'
  onDragStart?: (e: DragEvent<HTMLElement>) => void
  onPointerDown?: (e: PointerEvent<HTMLElement>) => void
  /** Where it sits down the block's side. */
  className?: string
  title?: string
}) {
  const visible = shown ?? selected
  return (
    <span
      draggable={!onPointerDown}
      role="button"
      tabIndex={-1}
      aria-label="Drag to move"
      title={title}
      onDragStart={onPointerDown ? undefined : onDragStart}
      onPointerDown={onPointerDown}
      onClick={(e) => e.stopPropagation()}
      className={`absolute -left-[18px] z-20 flex h-7 w-3.5 cursor-grab select-none items-center justify-center rounded-[5px] text-muted-foreground/70 transition-opacity after:absolute after:inset-y-0 after:left-full after:w-1 after:content-[''] hover:bg-foreground/6 hover:text-muted-foreground focus-visible:opacity-100 active:cursor-grabbing sm:-left-5 sm:w-4 [@media(hover:none)]:hidden ${className} ${
        visible ? 'opacity-100' : shown !== undefined ? 'opacity-0' : reveal === 'row' ? 'opacity-0 group-hover/row:opacity-100' : 'opacity-0 group-hover/card:opacity-100'
      }`}
    >
      <GripDots />
    </span>
  )
}

/** Six dots, two across and three down (⠿). */
function GripDots() {
  return (
    <svg aria-hidden data-grip="" viewBox="0 0 8 14" width="8" height="14" fill="currentColor">
      {[2, 7, 12].map((y) => (
        <g key={y}>
          <circle cx="1.75" cy={y} r="1.3" />
          <circle cx="6.25" cy={y} r="1.3" />
        </g>
      ))}
    </svg>
  )
}

export function ThoughtBlockView({
  block,
  selected,
  showTimes,
  sourceFormOpen,
  onSelect,
  onKeyDown,
  onRemove,
  onConvert,
  onEditSource,
  onCloseSource,
  onSaveSource,
  onDragStart,
}: SelectableProps &
  SourceProps & {
    block: ThoughtBlock
    showTimes: boolean
    onConvert: (to: 'text' | 'pullquote') => void
    /** Its grip: drag to move it, or beside another block as a column. */
    onDragStart?: (e: DragEvent<HTMLElement>) => void
  }) {
  const hasSource = !!(block.source?.title || block.source?.url)
  return (
    <div
      data-selectable={block.id}
      tabIndex={0}
      role="group"
      aria-label="Sourced thought"
      onClick={(e) => {
        e.stopPropagation()
        onSelect()
      }}
      onKeyDown={onKeyDown}
      // A Burrow card (the app's ThoughtCard surface), lit with the rose ring when selected.
      className={`group/card relative mb-[26px] flex flex-col gap-2 rounded-2xl border border-card-edge bg-card px-5 pb-4 pt-[18px] outline-none transition-shadow ${
        selected ? 'ring-2 ring-accent/40' : 'hover:shadow-lg hover:shadow-accent/5'
      }`}
    >
      {onDragStart && <DragGrip selected={!!selected} onDragStart={onDragStart} />}
      {selected && (
        <BlockToolbar label="Thought options">
          <ToolbarLabel>Sourced thought</ToolbarLabel>
          <ToolbarButton onClick={() => onConvert('pullquote')}>Pull quote</ToolbarButton>
          <ToolbarButton onClick={() => onConvert('text')}>Paragraph</ToolbarButton>
          <ToolbarDivider />
          {block.source?.url && (
            <ToolbarButton accent onClick={() => window.open(block.source!.url, '_blank', 'noopener,noreferrer')}>
              Open source
            </ToolbarButton>
          )}
          <ToolbarButton onClick={onEditSource}>{hasSource ? 'Edit source' : 'Add source'}</ToolbarButton>
          <RemoveButton onClick={onRemove} />
        </BlockToolbar>
      )}
      {showTimes && block.capturedAt && (
        <div className="font-sans text-[11.5px] font-medium text-muted-foreground">
          <Meta capturedAt={block.capturedAt} kind={block.kind} />
        </div>
      )}
      <p className="wr-t-thought m-0 text-foreground">{block.text}</p>
      {hasSource ? (
        <SourceLine source={block.source!} />
      ) : (
        !block.own && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              onSelect()
              onEditSource()
            }}
            className="self-start font-sans text-[13px] font-medium text-accent hover:underline"
          >
            No source yet — add one, or mark it as yours
          </button>
        )
      )}
      {sourceFormOpen && <SourceForm initial={block.source} own={!!block.own} onCancel={onCloseSource} onSave={onSaveSource} />}
    </div>
  )
}

/** [ and ] (or Alt+← / Alt+→) resize a selected image a step at a time: +1 bigger, −1 smaller, 0 not a resize key. */
function resizeKey(e: KeyboardEvent<HTMLElement>): 1 | -1 | 0 {
  if (e.metaKey) return 0
  // [ and ] need AltGr (Ctrl+Alt) on some layouts, so other modifiers don't matter.
  if (e.key === ']') return 1
  if (e.key === '[') return -1
  if (e.altKey && !e.ctrlKey && !e.shiftKey && e.key === 'ArrowRight') return 1
  if (e.altKey && !e.ctrlKey && !e.shiftKey && e.key === 'ArrowLeft') return -1
  return 0
}

/** The resize handle's arrow keys (it's a slider): steps in percent, or the ends. */
const HANDLE_STEPS: Record<string, number> = { ArrowRight: 1, ArrowUp: 1, ArrowLeft: -1, ArrowDown: -1, PageUp: 10, PageDown: -10 }

interface CornerDrag {
  pointer: number
  x: number
  y: number
  /** The width it started at, measured (percent of the column). */
  from: number
  /** The column's (or cell's) width in px. */
  column: number
  /** Px of width per px of pointer travel across: 2 for a centred image (it grows both ways), ±1 for a float. */
  k: number
  /** The photo box's size when the drag began, in px. */
  box: { width: number; height: number }
  /** The photo's own aspect (0 while it hasn't loaded: then only the width follows). */
  natural: number
  /** The frame it had (its zoom and focus are kept; only the shape follows the corner). */
  frame: ImageFrame
  /** Past the slop: it's a drag, not a click. */
  moving: boolean
  /** In a column cell side by side: its column, which grows once the image fills it (null elsewhere). */
  sizer: ColumnSizer | null
}

/**
 * The column an image sits in, sized from its corner handle (Niki, 10/2):
 * past the cell's full width the image takes its column with it, moving
 * the gutter. `grow` is px of pointer travel (0 = back as it was), shown
 * live; `commit` keeps it.
 */
export interface ColumnSizer {
  grow: (px: number) => void
  commit: () => void
  cancel: () => void
}

/** A width inside the placement's range, not rounded (the corner mid-drag; it rounds when it's let go). */
function clampLive(placement: ImagePlacement, width: number): number {
  const r = widthRange(placement)
  return Number.isFinite(width) ? Math.min(r.max, Math.max(r.min, width)) : r.def
}

/** An image that fills at least this share of its column counts as edge to edge. */
const FILLS = 0.985

/** The tallest and longest a corner drag shapes a photo box (width / height): a tall portrait to a long band. */
const SHAPE_MIN = 0.3
const SHAPE_MAX = 6

/**
 * An image in the composer, laid out as readers get it (components/
 * imageLayout.ts), and handled as in Composer lab's wrap tool. From md up
 * the hand is always on: press any image and move it anywhere, the text
 * reflowing round it as it goes (useImageHand), outlined while it moves.
 * Hovered or selected, it shows a small Reposition · Remove pill inside its
 * top-left corner, the six-dot grip against its left edge, and a round
 * handle on its bottom corner on the text's side. Nothing else: no outline
 * at rest, no placement buttons, no size readout. The caption is written in
 * place over the photo's bottom-left corner.
 *
 * The corner handle follows the pointer both ways at once, the same on a
 * wrap, in the column and in a column cell (Niki, 10/5): across sets the
 * width, up and down the height, and the photo covers whatever box that
 * makes, keeping its zoom and focus (so a corner-wise drag that keeps the
 * shape just resizes it, and a straight one crops). In a cell, past the
 * cell's width the column grows with it. Reposition in the toolbar moves the
 * photo inside the box.
 */
export function ImageBlockView({
  block,
  selected,
  showTimes,
  onSelect,
  onKeyDown,
  onRemove,
  onCaption,
  onPlace,
  onCrop,
  onReplace,
  onReplaceFile,
  onResize,
  onReshape,
  onDragStart,
  canWrap = true,
  columnSizer,
  onHandStart,
  onHandMove,
  onHandCancel,
  onHandEnd,
}: Omit<SelectableProps, 'onSelect'> & {
  block: ImageBlock
  showTimes: boolean
  /** Select the image; `focus` moves keyboard focus to it (not when clicking into its caption). */
  onSelect: (focus?: boolean) => void
  onCaption: (caption: string) => void
  onPlace: (placement: ImagePlacement) => void
  /** Open crop & position (the crop applies to every placement; column images take its shape). */
  onCrop: () => void
  /** Pick another photo for this block (the file picker). */
  onReplace: () => void
  /** An image file dropped on the photo: it replaces it, as Replace would. */
  onReplaceFile: (file: File) => void
  /** A new width in percent of the column; null = the placement's default. */
  onResize: (width: number | null) => void
  /** A new crop and width together (the corner handle's crop). */
  onReshape: (frame: ImageFrame | null, width: number | null) => void
  onDragStart: (e: DragEvent<HTMLElement>) => void
  /** Wrapping is on offer (not inside a column cell). */
  canWrap?: boolean
  /** In a column cell: its column, for the corner handle to widen (null when the cells are stacked). */
  columnSizer?: () => ColumnSizer | null
  /** The hand: a drag began, moved the image somewhere, or was called off (Esc). */
  onHandStart?: () => void
  onHandMove: (t: HandTarget) => void
  onHandCancel?: () => void
  onHandEnd?: () => void
}) {
  // In a column cell a wrap shows (and sizes) as a column image; its stored placement waits for it to come back out.
  const stored = block.placement ?? 'inline'
  const placement: ImagePlacement = !canWrap && (stored === 'wrap-left' || stored === 'wrap-right') ? 'inline' : stored
  const figureRef = useRef<HTMLElement>(null)
  // The hand needs room beside the image to wrap it: md up, and not in a cell.
  const wide = useMediaQuery('(min-width: 768px)')
  const handOn = canWrap && wide
  // The lab's affordances come with the mouse (or pen) over the image, or with it selected.
  const [hovered, setHovered] = useState(false)
  // An image file dragged over the photo (from the desktop): dropped, it replaces it.
  const [fileOver, setFileOver] = useState(false)
  const hand = useImageHand({
    id: block.id,
    enabled: handOn,
    placement,
    offset: wrapOffset(block),
    onStart: onHandStart,
    onMove: onHandMove,
    onCancel: onHandCancel,
    onEnd: onHandEnd,
  })
  // The width and crop while the corner handle is being dragged, committed on release.
  const [live, setLive] = useState<{ width?: number | null; frame?: ImageFrame | null } | null>(null)
  const liveRef = useRef<typeof live>(null)
  const corner = useRef<CornerDrag | null>(null)
  const shown = live === null ? block : withLive(block, live)
  // Mid-drag, the width draws as a percent even at the default, so there's no step where the default's classes take over.
  const layout = imageLayout(shown, { cell: !canWrap, exact: live?.width !== undefined })
  const range = widthRange(placement)
  const wrapRight = placement === 'wrap-right'

  const setLiveShape = (v: typeof live) => {
    liveRef.current = v
    setLive(v)
  }

  const onFigureKey = (e: KeyboardEvent<HTMLElement>) => {
    const dir = resizeKey(e)
    if (selected && dir !== 0) {
      e.preventDefault()
      onResize(clampWidth(placement, imageWidth(block) + dir * WIDTH_STEP))
      return
    }
    // ← and → (no modifiers) move it along wrap left · column · wrap right, as the hand does.
    if (selected && handOn && e.target === e.currentTarget && !e.altKey && !e.ctrlKey && !e.metaKey && !e.shiftKey && (e.key === 'ArrowLeft' || e.key === 'ArrowRight')) {
      e.preventDefault()
      const next = stepPlacement(placement, e.key === 'ArrowLeft' ? -1 : 1)
      if (next !== placement) {
        // Eased as the hand's moves are (flip.ts).
        const before = snapshotLayout(block.id)
        flushSync(() => onPlace(next))
        playFlip(before, block.id)
      }
      return
    }
    onKeyDown(e)
  }

  /** The grip's drag shows the photo itself, a clean small copy, not a cut-out of the screen around it. */
  const onGripDrag = (e: DragEvent<HTMLElement>) => {
    onDragStart(e)
    const fig = figureRef.current
    const img = fig?.querySelector('img')
    const photo = img?.parentElement
    if (!img || !photo || !e.dataTransfer?.setDragImage) return
    const natural = img.naturalWidth > 0 && img.naturalHeight > 0 ? img.naturalWidth / img.naturalHeight : 0
    const box = photo.getBoundingClientRect()
    if (!(natural > 0 && box.width > 0 && box.height > 0)) return
    const f = safeFrame(block.frame)
    const aspect = layout.fit === 'shape' ? (f ? shapeOf(f) : natural) : box.width / box.height
    const ghost = photoGhost(img, visibleCrop(natural, aspect, f, f ? 0.5 : INLINE_REST_Y), aspect)
    if (!ghost) return
    document.body.appendChild(ghost)
    // Held by its top-left, where the grip is.
    e.dataTransfer.setDragImage(ghost, 14, 14)
    // The browser has taken its picture by the next task.
    setTimeout(() => ghost.remove(), 0)
  }

  // ── The corner handle: pointer capture, so a drag keeps going off the handle (mouse, pen or touch) ──
  const onHandleDown = (e: PointerEvent<HTMLElement>) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return
    const fig = figureRef.current
    // The row spans the column (or the cell); the figure's own parent is display: contents, with no box to measure.
    const column = (fig?.closest('[data-row]') ?? fig?.parentElement)?.getBoundingClientRect().width ?? 0
    const width = fig?.getBoundingClientRect().width ?? 0
    if (!(column > 0 && width > 0)) return
    e.preventDefault() // no text selection, no focus change
    e.stopPropagation()
    try {
      e.currentTarget.setPointerCapture?.(e.pointerId)
    } catch {
      /* not a live pointer (a synthetic event) */
    }
    const img = fig?.querySelector('img')
    const photo = img?.parentElement?.getBoundingClientRect()
    const natural = img && img.naturalWidth > 0 && img.naturalHeight > 0 ? img.naturalWidth / img.naturalHeight : 0
    const f = safeFrame(block.frame)
    corner.current = {
      pointer: e.pointerId,
      x: e.clientX,
      y: e.clientY,
      // Measured, not stored: on a phone a small image shows wider than its width.
      from: (width / column) * 100,
      column,
      k: layout.wrap ? (wrapRight ? -1 : 1) : 2,
      box: { width: photo?.width ?? 0, height: photo?.height ?? 0 },
      natural,
      // An unframed band rests at 'center 60%'; anything else at the middle.
      frame: { natural, zoom: f?.zoom ?? 1, focus: f?.focus ?? { x: 0.5, y: layout.fit === 'cover' ? INLINE_REST_Y : 0.5 } },
      moving: false,
      sizer: layout.wrap ? null : (columnSizer?.() ?? null),
    }
  }
  /**
   * Where the corner is `dx`, `dy` px from where it was pressed: the width
   * (percent of the column, inside the placement's range; past a cell's
   * edge its column grows instead) and, once the photo has loaded, a frame
   * shaped so the box is as tall as the pointer says. `settle`: whole
   * percents, as it's stored, with the shape worked out at that width.
   */
  const cornerAt = (c: CornerDrag, dx: number, dy: number, settle: boolean): { width: number; frame?: ImageFrame } => {
    const raw = c.from + ((dx * c.k) / c.column) * 100
    let width: number
    if (c.sizer && raw > 100) {
      // Full width in its cell: the rest of the drag widens the column (the corner keeps up with the pointer).
      const reach = ((100 - Math.min(100, c.from)) / 100) * (c.column / c.k)
      c.sizer.grow(dx - reach)
      width = 100
    } else if (c.sizer && c.from >= FILLS * 100) {
      // Already filling it: smaller makes the column smaller too, down to the narrowest it goes.
      c.sizer.grow(dx)
      width = 100
    } else {
      c.sizer?.grow(0)
      width = settle ? clampWidth(placement, raw) : clampLive(placement, raw)
    }
    if (!(c.natural > 0 && c.box.width > 0 && c.box.height > 0)) return { width }
    // The photo box's width as it will be: measured when the column moved with it, else its share of the column.
    const grown = c.sizer && width === 100 ? figureRef.current?.getBoundingClientRect().width : undefined
    const w = grown && grown > 0 ? grown : (width / c.from) * c.box.width
    const h = Math.max(MIN_CROP_BOX, c.box.height + dy)
    const shape = Math.min(SHAPE_MAX, Math.max(SHAPE_MIN, w / h))
    return { width, frame: { ...c.frame, shape: +shape.toFixed(4) } }
  }
  const onHandleMove = (e: PointerEvent<HTMLElement>) => {
    const c = corner.current
    if (!c || e.pointerId !== c.pointer) return
    const dx = e.clientX - c.x
    const dy = e.clientY - c.y
    if (!c.moving && !pastSlop(dx, dy, 4)) return
    c.moving = true
    setLiveShape(cornerAt(c, dx, dy, false))
  }
  const onHandleUp = (e: PointerEvent<HTMLElement>) => {
    const c = corner.current
    if (!c || e.pointerId !== c.pointer) return
    corner.current = null
    const v = c.moving ? cornerAt(c, e.clientX - c.x, e.clientY - c.y, true) : null
    setLiveShape(null)
    c.sizer?.commit()
    if (!v) return
    if (v.frame) onReshape(v.frame, v.width)
    else onResize(v.width)
  }
  const onHandleCancel = () => {
    if (!corner.current) return
    corner.current.sizer?.cancel()
    corner.current = null
    setLiveShape(null)
  }
  const onHandleKey = (e: KeyboardEvent<HTMLElement>) => {
    if (e.altKey || e.ctrlKey || e.metaKey) return
    const step = HANDLE_STEPS[e.key]
    const next = step !== undefined ? layout.width + step : e.key === 'Home' ? range.min : e.key === 'End' ? range.max : null
    if (next === null) return
    e.preventDefault()
    e.stopPropagation()
    onResize(clampWidth(placement, next))
  }

  const stamp = showTimes && block.capturedAt ? <Meta capturedAt={block.capturedAt} kind={block.kind} /> : null
  const caption = block.caption ?? ''
  const tools = (!!selected || hovered) && !hand.dragging
  return (
    <figure
      ref={figureRef}
      data-selectable={block.id}
      tabIndex={0}
      aria-label="Image"
      onClick={(e) => {
        e.stopPropagation()
        onSelect()
      }}
      onKeyDown={onFigureKey}
      onPointerEnter={(e) => {
        if (e.pointerType !== 'touch') setHovered(true)
      }}
      onPointerLeave={() => setHovered(false)}
      // A file from the desktop dropped on the photo replaces it, as on the empty cover. Only files are
      // taken here: blocks and thoughts dragged over it go on to the page's own drop lines.
      onDragOver={(e) => {
        if (!e.dataTransfer.types.includes('Files')) return
        e.preventDefault()
        e.stopPropagation()
        e.dataTransfer.dropEffect = 'copy'
        setFileOver(true)
      }}
      onDragLeave={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setFileOver(false)
      }}
      onDrop={(e) => {
        setFileOver(false)
        const file = e.dataTransfer.files?.[0]
        if (!file) return
        e.preventDefault()
        e.stopPropagation()
        onReplaceFile(file)
      }}
      {...hand.photo}
      // With its tools showing it comes up above its neighbours (a wrap is only z-1), so its grip is the one under
      // the pointer: not the "+ column" beside its row, nor the grip of the paragraph that starts level with it (z-20).
      style={tools ? { ...layout.style, zIndex: 25 } : layout.style}
      className={`relative m-0 mb-[26px] flex flex-col outline-none ${layout.figure} ${handOn ? (hand.dragging ? 'cursor-grabbing' : 'cursor-grab') : ''}`}
    >
      <span aria-live="polite" className="sr-only">
        {hand.said}
      </span>
      {selected && handOn && !hand.dragging && (
        // The hand, for touch: a finger on the photo scrolls the page, so it needs a way in of its own.
        <span
          {...hand.handle}
          role="img"
          aria-label="Drag to move it; sideways to wrap"
          title="Drag to move it, sideways to wrap the text around it"
          className="absolute left-1/2 top-1/2 z-20 hidden size-10 -translate-x-1/2 -translate-y-1/2 cursor-grab touch-none select-none items-center justify-center rounded-full bg-foreground/60 text-card shadow-[var(--shadow-chip)] backdrop-blur-md transition-colors hover:bg-foreground/75 pointer-coarse:flex"
        >
          <HandGlyph />
        </span>
      )}
      {tools && (
        <>
          {/* Where the hand is on, the grip is the hand too (Niki, 10/5): the same drag as pressing the photo. In a cell it moves the block. */}
          {handOn ? (
            <DragGrip selected={!!selected} shown className="top-1.5" title="Drag to move it; sideways to wrap" onPointerDown={hand.handle.onPointerDown} />
          ) : (
            <DragGrip selected={!!selected} shown className="top-1.5" title="Drag to move" onDragStart={onGripDrag} />
          )}
          <ImagePlacementToolbar onReposition={onCrop} onReplace={onReplace} onRemove={onRemove} />
        </>
      )}
      <FramedImage
        src={block.src}
        alt={block.caption}
        frame={shown.frame}
        fit={layout.fit}
        defaultPosition="center 60%"
        draggable={false}
        imgClassName={layout.img}
        // The lab's one mark: an accent ring while it moves (or a file to replace it is over it). Selected (by click or keyboard), the pill and handle show it.
        className={`rounded-[4px] bg-muted outline-offset-4 outline-accent ${hand.dragging || fileOver ? 'outline-2' : ''} ${layout.box}`}
      />
      {/* Nothing over the photo until there's a caption, or the image is selected to write one. */}
      {(caption.trim() !== '' || selected) && (
        <figcaption data-caption-overlay="" className={`${OVERLAY_CLS} pointer-events-none`}>
          <CaptionField
            value={caption}
            small={layout.wrap}
            width={layout.width}
            onChange={onCaption}
            onFocus={() => {
              if (!selected) onSelect(false)
            }}
          />
          {stamp && <span className={STAMP_CLS}>{stamp}</span>}
        </figcaption>
      )}
      {tools && (
        <span
          role="slider"
          tabIndex={0}
          aria-label="Resize image"
          aria-orientation="horizontal"
          aria-valuemin={range.min}
          aria-valuemax={range.max}
          aria-valuenow={layout.width}
          aria-valuetext={`${layout.width}% of the column`}
          title="Drag to size it: across for the width, up and down for the height"
          onPointerDown={onHandleDown}
          onPointerMove={onHandleMove}
          onPointerUp={onHandleUp}
          onPointerCancel={onHandleCancel}
          onLostPointerCapture={onHandleCancel}
          onKeyDown={onHandleKey}
          // The lab's handle: a 14px ring astride the photo's bottom corner on the text's side (bottom-left
          // on a wrap-right), reaching wider than it shows. Wraps are full width on phones whatever their
          // size, so there's nothing to drag there.
          className={`absolute -bottom-1.5 z-20 size-3.5 touch-none select-none rounded-full border-2 border-foreground bg-card outline-none before:absolute before:-inset-2 before:content-[''] focus-visible:ring-2 focus-visible:ring-accent/60 ${
            wrapRight ? '-left-1.5 cursor-nesw-resize' : '-right-1.5 cursor-nwse-resize'
          } ${layout.wrap ? 'hidden md:block' : 'block'}`}
        />
      )}
    </figure>
  )
}

/** The block as the corner handle is showing it mid-drag. */
function withLive(block: ImageBlock, live: { width?: number | null; frame?: ImageFrame | null }): ImageBlock {
  const next = { ...block }
  if (live.frame === null) delete next.frame
  else if (live.frame) next.frame = live.frame
  if (live.width === null) delete next.width
  else if (live.width !== undefined) next.width = live.width
  return next
}

/** How big the grip's drag picture of a photo is, at most, along its longer side (px). */
const GHOST_MAX = 180

/**
 * The picture a dragged image shows under the pointer: the part of the photo
 * the image shows (`crop`), drawn small on a canvas off screen. The browser's
 * own picture of the <img> was a cut-out of the screen round it (a framed
 * photo is bigger than its box, which clips it), so it showed whatever was
 * there; this is just the photo. Null if it can't be drawn.
 */
function photoGhost(img: HTMLImageElement, crop: CropRect, aspect: number): HTMLCanvasElement | null {
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext?.('2d')
  if (!ctx) return null
  const w = aspect >= 1 ? GHOST_MAX : Math.round(GHOST_MAX * aspect)
  const h = aspect >= 1 ? Math.round(GHOST_MAX / aspect) : GHOST_MAX
  const dpr = Math.min(2, window.devicePixelRatio || 1)
  canvas.width = Math.round(w * dpr)
  canvas.height = Math.round(h * dpr)
  Object.assign(canvas.style, {
    position: 'fixed',
    left: '-10000px',
    top: '0',
    width: `${w}px`,
    height: `${h}px`,
    borderRadius: '6px',
    pointerEvents: 'none',
  })
  const nw = img.naturalWidth
  const nh = img.naturalHeight
  try {
    ctx.drawImage(img, crop.x * nw, crop.y * nh, crop.w * nw, crop.h * nh, 0, 0, canvas.width, canvas.height)
  } catch {
    return null
  }
  return canvas
}

/**
 * The caption, written in place on the photo: a transparent field that
 * wraps and grows with its text, as the reader's caption does. One line of
 * thought, so Enter doesn't add a line break.
 */
function CaptionField({
  value,
  small,
  width,
  onChange,
  onFocus,
}: {
  value: string
  small: boolean
  /** The image's width, to re-fit when it changes. */
  width: number
  onChange: (caption: string) => void
  onFocus: () => void
}) {
  const ref = useRef<HTMLTextAreaElement>(null)
  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    const fit = () => {
      el.style.height = '0px'
      el.style.height = `${el.scrollHeight}px`
    }
    fit()
    // Re-fit when the field's width changes (a window resize, the thoughts panel); a frame later, so it never loops.
    if (typeof ResizeObserver === 'undefined') return
    let last = el.clientWidth
    let frame = 0
    const ro = new ResizeObserver(() => {
      if (el.clientWidth === last) return
      last = el.clientWidth
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(fit)
    })
    ro.observe(el)
    return () => {
      ro.disconnect()
      cancelAnimationFrame(frame)
    }
  }, [value, small, width])
  return (
    <textarea
      ref={ref}
      rows={1}
      value={value}
      onChange={(e) => onChange(e.target.value.replace(/\s*\n\s*/g, ' '))}
      onClick={(e) => e.stopPropagation()}
      onFocus={onFocus}
      onKeyDown={(e) => {
        e.stopPropagation()
        if (e.key === 'Enter') e.preventDefault()
      }}
      placeholder="Add a caption"
      aria-label="Caption"
      className={`${captionCls(small)} pointer-events-auto block resize-none overflow-hidden rounded-[3px] bg-transparent p-0 outline-none placeholder:text-on-scrim/75 focus-visible:ring-1 focus-visible:ring-card/50`}
    />
  )
}

export function LinkCardBlockView({
  block,
  selected,
  onSelect,
  onKeyDown,
  onRemove,
  onChange,
  onDragStart,
}: SelectableProps & {
  block: LinkCardBlock
  onChange: (patch: Partial<LinkCardBlock>) => void
  /** Its grip: drag to move it, or beside another block as a column. */
  onDragStart?: (e: DragEvent<HTMLElement>) => void
}) {
  const stop = (e: { stopPropagation: () => void }) => e.stopPropagation()
  return (
    <div
      data-selectable={block.id}
      tabIndex={0}
      role="group"
      aria-label="Link card"
      onClick={(e) => {
        e.stopPropagation()
        onSelect()
      }}
      onKeyDown={onKeyDown}
      className={`group/card relative mb-[26px] flex flex-col gap-1.5 rounded-2xl border border-card-edge bg-card px-5 py-4 outline-none transition-shadow ${selected ? 'ring-2 ring-accent/40' : ''}`}
    >
      {onDragStart && <DragGrip selected={!!selected} onDragStart={onDragStart} />}
      {selected && (
        <BlockToolbar label="Link card options">
          <ToolbarLabel>Link card</ToolbarLabel>
          <ToolbarButton accent onClick={() => window.open(block.url, '_blank', 'noopener,noreferrer')}>
            Open link
          </ToolbarButton>
          <RemoveButton onClick={onRemove} />
        </BlockToolbar>
      )}
      <LinkSite url={block.url} />
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <input
          value={block.title}
          onChange={(e) => onChange({ title: e.target.value })}
          onClick={stop}
          onKeyDown={stop}
          aria-label="Card title"
          placeholder="Title"
          className="bg-transparent font-display text-[18px]/[1.35] text-primary outline-none placeholder:text-muted-foreground/60"
        />
        <input
          value={block.description ?? ''}
          onChange={(e) => onChange({ description: e.target.value })}
          onClick={stop}
          onKeyDown={stop}
          aria-label="Card description"
          placeholder="Add a short description"
          className="bg-transparent font-display text-[14px]/[1.5] text-foreground/60 outline-none placeholder:text-muted-foreground/60"
        />
      </div>
    </div>
  )
}

export function DividerView({ block, selected, onSelect, onKeyDown, onRemove }: SelectableProps & { block: DividerBlock }) {
  return (
    <div
      data-selectable={block.id}
      tabIndex={0}
      role="separator"
      aria-label="Divider"
      onClick={(e) => {
        e.stopPropagation()
        onSelect()
      }}
      onKeyDown={onKeyDown}
      className={`relative clear-both my-10 flex justify-center gap-2.5 rounded py-2 outline-none ${selected ? 'bg-accent/10' : ''}`}
    >
      {selected && (
        <BlockToolbar label="Divider options">
          <ToolbarLabel>Divider</ToolbarLabel>
          <RemoveButton onClick={onRemove} />
        </BlockToolbar>
      )}
      {[0, 1, 2].map((i) => (
        <span key={i} className="size-1 rounded-full bg-accent" />
      ))}
    </div>
  )
}

/** An open hand: the drag-to-wrap handle. */
function HandGlyph() {
  return (
    <svg aria-hidden viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 11V6a2 2 0 0 0-4 0" />
      <path d="M14 10V4a2 2 0 0 0-4 0v2" />
      <path d="M10 10.5V6a2 2 0 0 0-4 0v8" />
      <path d="M18 8a2 2 0 1 1 4 0v6a8 8 0 0 1-8 8h-2c-2.8 0-4.5-.86-5.99-2.34l-3.6-3.6a2 2 0 0 1 2.83-2.82L7 15" />
    </svg>
  )
}
